/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from "classnames";
import Image from "next/image";

import HamburgerMenuIcon from "static/icons/hamburger_menu.svg";
import CloseIcon from "static/icons/close.svg";
import BackIcon from "static/icons/back.svg";
import ArrowIcon from "static/icons/Arrow.svg";
import classes from "./Button.module.css";
import useTranslation from "next-translate/useTranslation";
import Spinner from "../Spinner/Spinner";
import { Menu } from "lucide-react";

type ButtonProps = {
  content: string | JSX.Element;
  type?: "button" | "submit" | "reset";
  onClick?: any;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
};

const Button = ({
  content,
  type = "submit",
  onClick,
  disabled,
  className,
  loading,
}: ButtonProps) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={classNames(classes.button, className)}
  >
    {loading ? (
      <div className={classes.buttonSpinner}>
        <Spinner variant="white" />
      </div>
    ) : (
      content
    )}
  </button>
);

export default Button;

export const OutlinedButton = ({
  content,
  type = "button",
  onClick,
  disabled,
  className,
}: ButtonProps) => (
  <Button
    type={type}
    content={content}
    onClick={onClick}
    disabled={disabled}
    className={classNames(classes.outlinedButton, className)}
  />
);

export const RedOutlinedButton = ({
  content,
  type = "button",
  onClick,
  disabled,
  className,
}: ButtonProps) => (
  <Button
    type={type}
    content={content}
    onClick={onClick}
    disabled={disabled}
    className={classNames(classes.redOutlinedButton, className)}
  />
);

export const FilledButton = ({
  content,
  type = "button",
  onClick,
  disabled,
  className,
  loading,
}: ButtonProps) => (
  <Button
    type={type}
    content={content}
    onClick={onClick}
    disabled={disabled}
    className={classNames(classes.filledButton, className)}
    loading={loading}
  />
);
export const TextButton = ({
  type = "submit",
  onClick,
  disabled,
  className,
  content,
}: ButtonProps) => (
  <Button
    content={content}
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={classNames(classes.textButton, className)}
  />
);

type IconButtonProps = {
  onClick?: () => void;
  className?: string;
};

type HamburgerButtonProps = IconButtonProps & {
  openedMenu?: boolean;
};

export const HamburgerButton = ({
  openedMenu,
  onClick,
  className,
}: HamburgerButtonProps) => {
  const { t } = useTranslation("common");
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(classes.iconButton, className)}
    >
      {openedMenu ? (
        <Image src={CloseIcon} alt={t("openMenu")} layout="responsive" />
      ) : (
        <Menu color="#000" size={24} />
      )}
    </button>
  );
};

type BackButtonProps = IconButtonProps & {
  text?: string;
  desktop?: boolean;
};

export const BackButton = ({
  onClick,
  text,
  desktop,
  className,
}: BackButtonProps) => {
  const { t } = useTranslation("common");

  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(classes.backBtn, className)}
    >
      <span
        className={classNames(classes.icon, { [classes.desktop]: desktop })}
      >
        <Image
          src={desktop ? ArrowIcon : BackIcon}
          alt={t("back")}
          layout="responsive"
        />
      </span>
      {text && <span className={classes.text}>{text}</span>}
    </button>
  );
};

export const CloseButton = ({ onClick, className }: IconButtonProps) => {
  const { t } = useTranslation("common");
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(classes.iconButton, className)}
    >
      <Image src={CloseIcon} alt={t("close")} layout="responsive" />
    </button>
  );
};
